import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, CheckCircle2, AlertCircle, Link2, ChevronDown, ChevronRight,
  Database, Users, Calendar, Home, Euro, FileText, Shield, Activity,
  Wrench, ClipboardList, Building2, HeartPulse, Phone, MessageSquare,
  Eye, PhoneCall, CreditCard, Landmark, Mail, Download, RefreshCw,
  ArrowRightLeft, Square, CheckSquare, XCircle, Loader2, Sparkles,
  AlertTriangle, Filter
} from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

// ═══════════════════════════════════════
// STATIC VARIABLES (from existing CRM list)
// ═══════════════════════════════════════
const STATIC_VARIABLES = [
  { key: "client_id", label: "ID Client", desc: "Identifiant unique CRM du client", exemple: "SAN_20260228_9EDD", cat: "Identite Client", status: "ok", source: "pipeline_clients" },
  { key: "civilite", label: "Civilite", desc: "Civilite du client (Monsieur/Madame)", exemple: "Madame", cat: "Identite Client", status: "ok", source: "pipeline_clients + client_details.civilit" },
  { key: "nom", label: "Nom", desc: "Nom de famille du client", exemple: "BENHAMOU", cat: "Identite Client", status: "ok", source: "pipeline_clients" },
  { key: "prenom", label: "Prenom", desc: "Prenom du client", exemple: "Serge", cat: "Identite Client", status: "ok", source: "pipeline_clients + client_details.pr_nom" },
  { key: "telephone", label: "Telephone portable", desc: "Numero de portable principal du client", exemple: "0666666666", cat: "Coordonnees", status: "ok", source: "pipeline_clients + client_details.tel_client" },
  { key: "telephone_fixe", label: "Telephone fixe", desc: "Numero de fixe du client (si renseigne)", exemple: "0160637687", cat: "Coordonnees", status: "ok", source: "pipeline_clients + client_details.tel_fixe_qhare" },
  { key: "email", label: "Email", desc: "Adresse email principale du client", exemple: "serge@email.fr", cat: "Coordonnees", status: "ok", source: "pipeline_clients + client_details.email_qhare" },
  { key: "adresse", label: "Adresse", desc: "Adresse postale complete du client", exemple: "30 rue Erard", cat: "Coordonnees", status: "ok", source: "pipeline_clients" },
  { key: "ville", label: "Ville", desc: "Ville du client", exemple: "Paris", cat: "Coordonnees", status: "ok", source: "pipeline_clients" },
  { key: "code_postal", label: "Code postal", desc: "Code postal du client", exemple: "75010", cat: "Coordonnees", status: "ok", source: "pipeline_clients" },
  { key: "dpt", label: "Departement", desc: "Numero du departement", exemple: "75", cat: "Coordonnees", status: "ok", source: "pipeline_clients" },
  { key: "etape_nom", label: "Etape pipeline", desc: "Etape actuelle du client dans le pipeline de vente", exemple: "LEAD, R1 PLANIFIE", cat: "Pipeline & Qualification", status: "ok", source: "pipeline_clients" },
  { key: "source", label: "Source du lead", desc: "Canal d'acquisition par lequel le client est arrive", exemple: "Google Ads, clubtravaux, ringover", cat: "Pipeline & Qualification", status: "ok", source: "pipeline_clients" },
  { key: "statut_label", label: "Statut qualification", desc: "Statut lisible de la qualification du client", exemple: "N'a pas son RFR, Veut reflechir", cat: "Pipeline & Qualification", status: "ok", source: "pipeline_clients" },
  { key: "date_rappel", label: "Date rappel", desc: "Date du prochain rappel planifie par le telepro", exemple: "2026-03-02", cat: "Phase Telepro", status: "ok", source: "pipeline_clients" },
  { key: "date_r1", label: "Date R1", desc: "Date du rendez-vous R1 (visite technicien chez le client)", exemple: "8/3/2026", cat: "Phase R1 - Technicien", status: "ok", source: "pipeline_clients + client_details.date_r1" },
  { key: "heure_r1", label: "Heure R1", desc: "Heure du rendez-vous R1", exemple: "14:00", cat: "Phase R1 - Technicien", status: "ok", source: "pipeline_clients" },
  { key: "telepro", label: "Telepro (id)", desc: "Identifiant ou nom court du telepro assigne", exemple: "sdi, fatima", cat: "Intervenants - Telepro", status: "ok", source: "pipeline_clients" },
  { key: "technicien", label: "Technicien nom", desc: "Nom du technicien assigne au RDV R1", exemple: "Anthony, Yaniv", cat: "Intervenants - Technicien", status: "ok", source: "pipeline_clients + client_details" },
  { key: "assigned_to", label: "Personne assignee", desc: "Email de la personne actuellement en charge du dossier", exemple: "sdi@sani-adapt.fr", cat: "Intervenants - Gestion", status: "ok", source: "pipeline_clients" },
  { key: "created_at", label: "Date creation fiche", desc: "Date de creation de la fiche client", exemple: "28/02/2026", cat: "Dates Systeme", status: "ok", source: "pipeline_clients" },
  { key: "updated_at", label: "Derniere modification", desc: "Date de derniere modification", exemple: "07/03/2026", cat: "Dates Systeme", status: "ok", source: "pipeline_clients" },
];

const CATEGORY_ICONS = {
  "Identite Client": Users,
  "Coordonnees": Phone,
  "Pipeline & Qualification": Activity,
  "Phase Telepro": Phone,
  "Phase R1 - Technicien": Calendar,
  "Phase R2 - Architecte": Calendar,
  "Phase VT AMO": Calendar,
  "Dossier ANAH": FileText,
  "Phase Chantier": Wrench,
  "Controle Qualite": ClipboardList,
  "Negociation": MessageSquare,
  "Intervenants - Telepro": Users,
  "Intervenants - Technicien": Users,
  "Intervenants - Architecte": Users,
  "Intervenants - AMO": Users,
  "Intervenants - Gestion": Users,
  "Logement": Home,
  "Logement - Detail": Home,
  "MaPrimeRenov": Shield,
  "Renovation Energie": Shield,
  "Devis": FileText,
  "Montants & Financier": Euro,
  "Aides": Euro,
  "Reste a Charge": Euro,
  "Encaissements & Paiements": Euro,
  "Avances & Soldes ANAH": Euro,
  "Financement": Euro,
  "Marge & Rentabilite": Euro,
  "Facturation Pennylane": Euro,
  "Facturation": Euro,
  "Telephonie Ringover": PhoneCall,
  "Conditions de Paiement": CreditCard,
  "Prescripteurs": Landmark,
  "Historique Communications": Mail,
  "SLA - Suivi Parcours": Activity,
  "Personne de confiance": HeartPulse,
  "Societe": Building2,
  "SAV & Apres travaux": ClipboardList,
  "Dates Systeme": Database,
  "Qhare (Legacy)": Database,
  "Calculs internes (tech)": Wrench,
  "Autre / Non classifie": AlertCircle,
};

const MIGRATION_STATUSES = {
  migrated:   { label: "Migre",        color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-400" },
  redirect:   { label: "Redirect",     color: "bg-sky-500/15 text-sky-400 border-sky-500/30",           dot: "bg-sky-400" },
  to_create:  { label: "A creer",      color: "bg-amber-500/15 text-amber-400 border-amber-500/30",     dot: "bg-amber-400" },
  ignored:    { label: "Ignore",       color: "bg-slate-500/15 text-slate-400 border-slate-500/30",      dot: "bg-slate-400" },
  pending:    { label: "En attente",   color: "bg-violet-500/15 text-violet-400 border-violet-500/30",   dot: "bg-violet-400" },
  data_leak:  { label: "Fuite donnee", color: "bg-red-500/15 text-red-400 border-red-500/30",            dot: "bg-red-400" },
};

const TABS = [
  { id: "variables", label: "Variables CRM" },
  { id: "migration", label: "Migration Sheet → Mongo" },
  { id: "data_leaks", label: "Fuites de donnees" },
];


// ═══════════════════════════════════════
// STATUS BADGE COMPONENT
// ═══════════════════════════════════════
const StatusBadge = ({ status, config }) => {
  const st = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${st.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
};


// ═══════════════════════════════════════
// TAB: VARIABLES CRM (Original view, simplified)
// ═══════════════════════════════════════
const VariablesTab = ({ search }) => {
  const [collapsedCats, setCollapsedCats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = useMemo(() => {
    return STATIC_VARIABLES.filter(v => {
      const matchSearch = !search || v.key.includes(search.toLowerCase()) || v.label.toLowerCase().includes(search.toLowerCase()) || v.desc.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || v.status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [search, filterStatus]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(v => {
      if (!map[v.cat]) map[v.cat] = [];
      map[v.cat].push(v);
    });
    return map;
  }, [filtered]);

  const STATUS_CONFIG = {
    ok: { label: "En base", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-400" },
    linked: { label: "Accessible", color: "bg-sky-500/15 text-sky-400 border-sky-500/30", dot: "bg-sky-400" },
    missing: { label: "A creer", color: "bg-amber-500/15 text-amber-400 border-amber-500/30", dot: "bg-amber-400" },
  };

  const counts = useMemo(() => {
    const c = { ok: 0, linked: 0, missing: 0 };
    STATIC_VARIABLES.forEach(v => c[v.status]++);
    return c;
  }, []);

  const toggleCat = (name) => setCollapsedCats(p => ({ ...p, [name]: !p[name] }));

  return (
    <div className="space-y-3">
      <div className="flex gap-2 text-xs flex-wrap">
        {[
          { val: 'all', label: 'Toutes', count: STATIC_VARIABLES.length },
          { val: 'ok', label: 'En base', count: counts.ok },
          { val: 'linked', label: 'Accessibles', count: counts.linked },
          { val: 'missing', label: 'A creer', count: counts.missing },
        ].map(f => (
          <button key={f.val} data-testid={`var-filter-${f.val}`} onClick={() => setFilterStatus(f.val)}
            className={`px-3 py-1.5 rounded-md border transition-colors ${
              filterStatus === f.val ? 'bg-blue-600/20 border-blue-500/40 text-blue-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
            }`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {Object.entries(grouped).map(([cat, items]) => {
        const Icon = CATEGORY_ICONS[cat] || Database;
        const isCollapsed = collapsedCats[cat];
        return (
          <div key={cat} className="rounded-lg border border-slate-700/60 bg-slate-800/40 overflow-hidden">
            <button onClick={() => toggleCat(cat)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-700/30 transition-colors text-left">
              {isCollapsed ? <ChevronRight size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
              <Icon size={15} className="text-slate-400" />
              <span className="text-sm font-semibold text-white flex-1">{cat}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">{items.length}</span>
            </button>
            {!isCollapsed && (
              <div className="border-t border-slate-700/40">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-700/30">
                      <th className="text-left py-2 px-4 font-medium w-[200px]">Variable</th>
                      <th className="text-left py-2 px-4 font-medium">Description</th>
                      <th className="text-left py-2 px-4 font-medium w-[140px]">Exemple</th>
                      <th className="text-left py-2 px-4 font-medium w-[100px]">Statut</th>
                      <th className="text-left py-2 px-4 font-medium w-[200px]">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(v => (
                      <tr key={v.key} className="border-b border-slate-700/20 hover:bg-slate-700/20 transition-colors">
                        <td className="py-2 px-4">
                          <code className="text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded text-[11px] font-mono">{`{{${v.key}}}`}</code>
                        </td>
                        <td className="py-2 px-4 text-slate-300">{v.desc}</td>
                        <td className="py-2 px-4 text-slate-500 font-mono text-[10px]">{v.exemple}</td>
                        <td className="py-2 px-4"><StatusBadge status={v.status} config={STATUS_CONFIG} /></td>
                        <td className="py-2 px-4 text-slate-500 text-[10px]">{v.source || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ═══════════════════════════════════════
// TAB: MIGRATION COCKPIT
// ═══════════════════════════════════════
const MigrationTab = ({ search, auditData, statuses, setStatuses, onSave, saving }) => {
  const [filterMigStatus, setFilterMigStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [collapsedCats, setCollapsedCats] = useState({});

  const rows = auditData?.rows || [];

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const ms = !search || r.sheet_key.toLowerCase().includes(search.toLowerCase()) || r.category.toLowerCase().includes(search.toLowerCase());
      const currentStatus = statuses[r.sheet_key]?.status || r.migration_status;
      const mf = filterMigStatus === 'all' || currentStatus === filterMigStatus;
      const cf = filterCategory === 'all' || r.category === filterCategory;
      return ms && mf && cf;
    });
  }, [rows, search, filterMigStatus, filterCategory, statuses]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      if (!map[r.category]) map[r.category] = [];
      map[r.category].push(r);
    });
    return Object.entries(map).sort((a, b) => {
      const sumA = a[1].reduce((s, r) => s + r.fill_count, 0);
      const sumB = b[1].reduce((s, r) => s + r.fill_count, 0);
      return sumB - sumA;
    });
  }, [filtered]);

  const categories = useMemo(() => [...new Set(rows.map(r => r.category))].sort(), [rows]);

  const statusCounts = useMemo(() => {
    const c = {};
    rows.forEach(r => {
      const s = statuses[r.sheet_key]?.status || r.migration_status;
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [rows, statuses]);

  const toggleSelect = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    const allKeys = new Set(filtered.map(r => r.sheet_key));
    setSelectedKeys(allKeys);
  };

  const deselectAll = () => setSelectedKeys(new Set());

  const bulkSetStatus = (newStatus) => {
    const updated = { ...statuses };
    selectedKeys.forEach(key => {
      updated[key] = { ...(updated[key] || {}), status: newStatus };
    });
    setStatuses(updated);
    setSelectedKeys(new Set());
  };

  const setRowStatus = (key, newStatus) => {
    setStatuses(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), status: newStatus }
    }));
  };

  const setRowNative = (key, field) => {
    setStatuses(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), native_field: field }
    }));
  };

  const acceptSuggestion = (row) => {
    setStatuses(prev => ({
      ...prev,
      [row.sheet_key]: {
        ...(prev[row.sheet_key] || {}),
        status: "migrated",
        native_field: row.suggested_native,
      }
    }));
  };

  const toggleCat = (name) => setCollapsedCats(p => ({ ...p, [name]: !p[name] }));

  const exportCSV = () => {
    window.open(`${API}/api/migration-audit/export-csv`, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div data-testid="migration-summary" className="flex gap-2 text-xs flex-wrap items-center">
        {Object.entries(MIGRATION_STATUSES).map(([k, v]) => (
          <button key={k} data-testid={`mig-filter-${k}`}
            onClick={() => setFilterMigStatus(filterMigStatus === k ? 'all' : k)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-colors ${
              filterMigStatus === k ? v.color + ' ring-1 ring-offset-1 ring-offset-slate-900' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${v.dot}`} />
            {statusCounts[k] || 0} {v.label}
          </button>
        ))}
        <button data-testid="mig-filter-all" onClick={() => setFilterMigStatus('all')}
          className={`px-2.5 py-1.5 rounded-md border transition-colors ${
            filterMigStatus === 'all' ? 'bg-blue-600/20 border-blue-500/40 text-blue-400' : 'bg-slate-800 border-slate-700 text-slate-400'
          }`}
        >
          Toutes ({rows.length})
        </button>
      </div>

      {/* Category filter + Actions bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-500" />
          <select data-testid="mig-cat-filter" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-md px-2 py-1.5 focus:border-blue-500 focus:outline-none"
          >
            <option value="all">Toutes categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {selectedKeys.size > 0 && (
          <div className="flex items-center gap-2 ml-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <span className="text-xs text-blue-400">{selectedKeys.size} selectionne(s)</span>
            <div className="flex gap-1">
              {Object.entries(MIGRATION_STATUSES).map(([k, v]) => (
                <button key={k} onClick={() => bulkSetStatus(k)} title={v.label}
                  className={`w-5 h-5 rounded flex items-center justify-center text-[8px] border ${v.color} hover:opacity-80`}
                >
                  {v.label[0]}
                </button>
              ))}
            </div>
            <button onClick={deselectAll} className="text-slate-500 hover:text-white"><XCircle size={14} /></button>
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <button data-testid="mig-select-all" onClick={selectedKeys.size === filtered.length ? deselectAll : selectAll}
            className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-md bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600"
          >
            {selectedKeys.size === filtered.length ? <CheckSquare size={12} /> : <Square size={12} />}
            {selectedKeys.size === filtered.length ? 'Deselectionner' : 'Tout selectionner'}
          </button>
          <button data-testid="mig-export-csv" onClick={exportCSV}
            className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-md bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600"
          >
            <Download size={12} /> CSV
          </button>
          <button data-testid="mig-save" onClick={onSave} disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Grouped rows */}
      <div className="space-y-2">
        {grouped.map(([cat, items]) => {
          const Icon = CATEGORY_ICONS[cat] || Database;
          const isCollapsed = collapsedCats[cat];
          const catStatusCounts = {};
          items.forEach(r => {
            const s = statuses[r.sheet_key]?.status || r.migration_status;
            catStatusCounts[s] = (catStatusCounts[s] || 0) + 1;
          });

          return (
            <div key={cat} className="rounded-lg border border-slate-700/60 bg-slate-800/40 overflow-hidden">
              <button onClick={() => toggleCat(cat)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-700/30 transition-colors text-left"
              >
                {isCollapsed ? <ChevronRight size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
                <Icon size={15} className="text-slate-400" />
                <span className="text-sm font-semibold text-white flex-1">{cat}</span>
                <div className="flex gap-1 text-[10px]">
                  {Object.entries(catStatusCounts).map(([s, c]) => {
                    const st = MIGRATION_STATUSES[s] || MIGRATION_STATUSES.pending;
                    return <span key={s} className={`px-1.5 py-0.5 rounded ${st.color}`}>{c}</span>;
                  })}
                  <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">{items.length}</span>
                </div>
              </button>

              {!isCollapsed && (
                <div className="border-t border-slate-700/40 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700/30">
                        <th className="py-2 px-2 w-8"></th>
                        <th className="text-left py-2 px-3 font-medium">Cle client_details</th>
                        <th className="text-left py-2 px-3 font-medium w-[80px]">Rempli</th>
                        <th className="text-left py-2 px-3 font-medium w-[120px]">Exemple</th>
                        <th className="text-left py-2 px-3 font-medium w-[140px]">Champ natif suggere</th>
                        <th className="text-left py-2 px-3 font-medium w-[140px]">Champ natif cible</th>
                        <th className="text-left py-2 px-3 font-medium w-[120px]">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(r => {
                        const currentStatus = statuses[r.sheet_key]?.status || r.migration_status;
                        const currentNative = statuses[r.sheet_key]?.native_field ?? r.native_field_target ?? '';
                        const isSelected = selectedKeys.has(r.sheet_key);

                        return (
                          <tr key={r.sheet_key} className={`border-b border-slate-700/20 transition-colors ${isSelected ? 'bg-blue-500/10' : 'hover:bg-slate-700/20'}`}>
                            <td className="py-2 px-2 text-center">
                              <button onClick={() => toggleSelect(r.sheet_key)} className="text-slate-500 hover:text-white">
                                {isSelected ? <CheckSquare size={14} className="text-blue-400" /> : <Square size={14} />}
                              </button>
                            </td>
                            <td className="py-2 px-3">
                              <code className="text-orange-400 bg-orange-500/10 px-1.5 py-0.5 rounded text-[11px] font-mono">
                                {r.sheet_key}
                              </code>
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${r.fill_pct}%` }} />
                                </div>
                                <span className="text-slate-400 text-[10px]">{r.fill_count}/{auditData?.clients_with_client_details}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-slate-500 font-mono text-[10px] max-w-[120px] truncate" title={r.sample_value}>
                              {r.sample_value || '—'}
                            </td>
                            <td className="py-2 px-3">
                              {r.suggested_native ? (
                                <button onClick={() => acceptSuggestion(r)} title={`Confiance: ${r.suggestion_confidence}`}
                                  className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded hover:bg-emerald-500/20 transition-colors"
                                >
                                  <Sparkles size={10} />
                                  {r.suggested_native}
                                  <span className="text-emerald-600">({Math.round(r.suggestion_confidence * 100)}%)</span>
                                </button>
                              ) : (
                                <span className="text-slate-600 text-[10px]">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3">
                              <input type="text" value={currentNative} onChange={e => setRowNative(r.sheet_key, e.target.value)}
                                placeholder="champ_natif"
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-300 placeholder-slate-600 focus:border-blue-500 focus:outline-none"
                              />
                            </td>
                            <td className="py-2 px-3">
                              <select value={currentStatus} onChange={e => setRowStatus(r.sheet_key, e.target.value)}
                                className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-[10px] text-slate-300 focus:border-blue-500 focus:outline-none"
                              >
                                {Object.entries(MIGRATION_STATUSES).map(([k, v]) => (
                                  <option key={k} value={k}>{v.label}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════
// TAB: DATA LEAKS
// ═══════════════════════════════════════
const DataLeaksTab = ({ dataLeaks }) => {
  if (!dataLeaks || dataLeaks.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <CheckCircle2 size={32} className="mx-auto mb-3 text-emerald-500" />
        <p>Aucune fuite de donnees detectee</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
        <AlertTriangle size={16} className="text-red-400" />
        <span className="text-sm text-red-400">{dataLeaks.length} fuite(s) de donnees detectee(s)</span>
      </div>

      {dataLeaks.map((leak, i) => (
        <div key={i} data-testid={`data-leak-${i}`} className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <code className="text-red-400 bg-red-500/10 px-2 py-0.5 rounded text-xs font-mono">{leak.endpoint}</code>
            <ArrowRightLeft size={14} className="text-red-400" />
            <code className="text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded text-xs font-mono">{leak.field_sent}</code>
          </div>
          <p className="text-sm text-slate-300">{leak.description}</p>
          <div className="flex gap-4 text-xs">
            <div>
              <span className="text-slate-500">Impact:</span>{' '}
              <span className="text-red-300">{leak.impact}</span>
            </div>
          </div>
          <div className="text-xs">
            <span className="text-slate-500">Correction:</span>{' '}
            <span className="text-emerald-300">{leak.fix}</span>
          </div>
        </div>
      ))}
    </div>
  );
};


// ═══════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════
const CrmVariablesList = () => {
  const [activeTab, setActiveTab] = useState('variables');
  const [search, setSearch] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [lastScan, setLastScan] = useState(null);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/migration-audit/scan`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAuditData(data);
        setLastScan(data.scanned_at);
        // Init statuses from saved data
        const saved = {};
        data.rows.forEach(r => {
          if (r.migration_status !== 'pending' || r.native_field_target) {
            saved[r.sheet_key] = {
              status: r.migration_status,
              native_field: r.native_field_target || '',
              notes: r.notes || '',
            };
          }
        });
        setStatuses(prev => ({ ...saved, ...prev }));
      }
    } catch (err) {
      console.error('Audit fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const saveStatuses = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/migration-audit/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ statuses }),
      });
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const totalVars = STATIC_VARIABLES.length;
  const migKeys = auditData?.total_named_keys || 0;
  const leaksCount = auditData?.data_leaks?.length || 0;

  return (
    <div data-testid="crm-variables-list" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Variables CRM & Migration</h2>
          <p className="text-sm text-slate-400 mt-1">
            Referentiel complet + cockpit de migration vers MongoDB natif
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {auditData && (
            <span className="text-[10px] text-slate-500">
              Scan: {new Date(lastScan).toLocaleString('fr-FR')}
            </span>
          )}
          <button data-testid="refresh-audit" onClick={fetchAudit} disabled={loading}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600 disabled:opacity-50"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Rafraichir
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-700">
        {TABS.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {tab.label}
            {tab.id === 'migration' && migKeys > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded bg-violet-500/20 text-violet-400">{migKeys}</span>
            )}
            {tab.id === 'data_leaks' && leaksCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded bg-red-500/20 text-red-400">{leaksCount}</span>
            )}
            {tab.id === 'variables' && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded bg-slate-700 text-slate-400">{totalVars}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input data-testid="crm-variables-search" type="text" placeholder="Rechercher..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Loading state */}
      {loading && !auditData && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-blue-400" />
          <span className="ml-2 text-slate-400">Scan en cours...</span>
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'variables' && <VariablesTab search={search} />}
      {activeTab === 'migration' && auditData && (
        <MigrationTab
          search={search}
          auditData={auditData}
          statuses={statuses}
          setStatuses={setStatuses}
          onSave={saveStatuses}
          saving={saving}
        />
      )}
      {activeTab === 'data_leaks' && <DataLeaksTab dataLeaks={auditData?.data_leaks} />}
    </div>
  );
};

export default CrmVariablesList;
